import { useContext, useMemo } from 'react';
import _ from 'lodash';

import EntityDefinitionsContext from 'contexts/EntityDefinitionsContext';
import getBaseViewFields from 'components/Grid/getBaseViewFields';
import isDefined from 'isDefined';
import useValueFormatters from 'hooks/useValueFormatters';
import { getCellRenderer } from 'hooks/useColumnDefs';

export interface DriverLeaderboardColumn {
  field: string;
  label: string;
  // false → right aligned (numbers, currency, percentages, etc.)
  isLeftAlign: boolean;
  // The grid "cell renderer" key (e.g. 'currencyCell', 'entityCell'). The
  // presentational table switches on this to pick bespoke markup.
  kind: string | undefined;
}

export interface DriverLeaderboardCell {
  field: string;
  kind: string | undefined;
  isLeftAlign: boolean;
  // The base-view-driven display string (currency/percentage/date/geo/…).
  formatted: string | number;
  // The un-formatted value, needed by cells that render more than text
  // (entity links, boolean icons, colour-coded numbers).
  rawValue: unknown;
  entityDefinition?: { entity: EntityDetails.Entity; app: EntityDetails.App };
  colourConfig?: FleetOps.BonusScoreColourConfig;
}

export interface DriverLeaderboardRow {
  key: string;
  cells: DriverLeaderboardCell[];
}

// Replicates the grid `valueGetter` (src/hooks/useColumnDefs.ts): supports a
// dotted `a.b` field where `data[a]` is an array (→ collect `b`s), an object
// (→ nested scalar) or a scalar.
export const getRawValue = ({
  field,
  document,
}: {
  field: string;
  document: ElasticDocument;
  // ElasticDocument values are untyped (any); return type mirrors that so the
  // value flows into formatField (typed union) and the render guards freely.
}): ElasticDocument[string] => {
  const dotSections = field.split('.');
  if (dotSections.length === 2) {
    const [lhs, rhs] = dotSections;
    const value = document[lhs];
    if (value === undefined) {
      return '-';
    }
    if (Array.isArray(value)) {
      const values = value
        .map((t) => (t ? t[rhs] : undefined))
        .filter((v) => v !== undefined && v !== null);
      return values.length === 0 ? '-' : values;
    }
    if (typeof value === 'object' && value !== null) {
      return value[rhs];
    }
    return value;
  }
  return document[field];
};

const getRowKey = (document: ElasticDocument, index: number): string => {
  const id = document.id ?? document._id;
  return typeof id === 'string' || typeof id === 'number'
    ? String(id)
    : `row-${index}`;
};

/**
 * Resolves the columns + per-cell descriptors for a Driver Leaderboard, reusing
 * the grid's base-view classification (`getCellRenderer`) and value formatting
 * (`useValueFormatters().formatField`). Rendering itself is bespoke — see
 * `DriverLeaderboardTable`.
 */
const useDriverLeaderboardCells = ({
  baseView,
  documents,
  dataType,
}: {
  baseView: FleetOps.BaseView;
  documents: ElasticDocument[];
  dataType: string;
}): {
  columns: DriverLeaderboardColumn[];
  rows: DriverLeaderboardRow[];
} => {
  const { getEntityAndDefaultAppForField } = useContext(
    EntityDefinitionsContext,
  );
  const { formatField } = useValueFormatters();

  return useMemo(() => {
    const visibleFieldsBase = getBaseViewFields(baseView).filter(
      (field) => field && field.isVisible,
    );
    const orderedFields = baseView.fieldOrder
      .map((f) => visibleFieldsBase.find((vf) => vf.field === f))
      .filter(isDefined);

    const resolved = orderedFields.map((fieldView) => {
      const entityDefinition = getEntityAndDefaultAppForField({
        field: fieldView.field,
      });
      const { cellRenderer, isLeftAlign } = getCellRenderer({
        isLaneCell: false,
        isCustomerLaneBoard: false,
        fieldView,
        isPerformanceBoard: false,
        entityField: undefined,
        board: undefined,
        isEntityField: !!entityDefinition,
        getEntityAndDefaultAppForField,
      });
      return {
        fieldView,
        entityDefinition,
        kind: cellRenderer,
        isLeftAlign,
        label: fieldView.nameAlias
          ? fieldView.nameAlias
          : _.startCase(fieldView.field),
      };
    });

    const columns: DriverLeaderboardColumn[] = resolved.map((r) => ({
      field: r.fieldView.field,
      label: r.label,
      isLeftAlign: r.isLeftAlign,
      kind: r.kind,
    }));

    const rows: DriverLeaderboardRow[] = documents.map((document, index) => ({
      key: getRowKey(document, index),
      cells: resolved.map((r) => {
        const rawValue = getRawValue({ field: r.fieldView.field, document });
        return {
          field: r.fieldView.field,
          kind: r.kind,
          isLeftAlign: r.isLeftAlign,
          formatted: formatField({
            field: r.fieldView.field,
            dataset: dataType,
            value: rawValue,
          }),
          rawValue,
          entityDefinition: r.entityDefinition,
          colourConfig: r.fieldView.bonusScoreColourConfig,
        };
      }),
    }));

    return { columns, rows };
  }, [
    baseView,
    documents,
    dataType,
    formatField,
    getEntityAndDefaultAppForField,
  ]);
};

export default useDriverLeaderboardCells;
